import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";

const BADGE = {
  Urgent:   { bg: "rgba(183,132,45,0.2)",  color: "#B8842D" },
  Currency: { bg: "rgba(183,132,45,0.2)",  color: "#B8842D" },
  Customs:  { bg: "rgba(166,70,70,0.15)",  color: "#A64646" },
  Safety:   { bg: "rgba(166,70,70,0.15)",  color: "#A64646" },
  Travel:   { bg: "rgba(62,107,74,0.15)",  color: "#3E6B4A" },
  Events:   { bg: "rgba(232,184,75,0.15)", color: "#E8B84B" },
};

const CACHE_KEY = "habano.updates.cache";

function readCache() {
  try {
    const raw = window.localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}
function writeCache(rows) {
  try { window.localStorage.setItem(CACHE_KEY, JSON.stringify(rows || [])); } catch { /* ignore */ }
}

function pickLang(field, lang) {
  if (!field) return "";
  if (typeof field === "string") return field;
  return field[lang] || field.en || field.es || "";
}

/**
 * HavanaUpdates — admin-curated bulletin section. Hidden when no active
 * non-expired updates exist. Reads from /api/updates and caches up to 3
 * items in localStorage so the section still renders offline.
 */
export { HavanaUpdates as default };
export function HavanaUpdates() {
  const [rows, setRows] = useState(() => readCache());
  const [loaded, setLoaded] = useState(rows !== null);
  const { lang } = useI18n();

  useEffect(() => {
    api.get("/updates", { params: { limit: 3 } })
      .then(({ data }) => { setRows(data || []); writeCache(data || []); setLoaded(true); })
      .catch(() => { setLoaded(true); });
  }, []);

  if (!loaded) return null;
  if (!rows || rows.length === 0) return null;

  return (
    <section className="px-6 mt-10" data-testid="havana-updates">
      <div className="eyebrow text-gold mb-3">Havana Updates</div>
      <div
        className="border p-4"
        style={{ background: "#171717", borderColor: "rgba(232,184,75,0.18)" }}
      >
        <div className="flex items-center gap-2 mb-4">
          <span className="w-2 h-2 rounded-full bg-gold animate-pulse" aria-hidden />
          <span className="eyebrow text-gold flex-1">Live updates</span>
          <Link to="/updates" data-testid="updates-see-all" className="text-[11px] uppercase tracking-widest text-gold hover:opacity-80">
            See all →
          </Link>
        </div>

        <ul className="divide-y divide-[rgba(232,184,75,0.12)]">
          {rows.slice(0, 3).map((u) => {
            const cat = BADGE[u.category] || BADGE.Events;
            const headline = pickLang(u.headline, lang);
            const short = pickLang(u.short_description, lang);
            return (
              <li key={u.id} className="py-3 first:pt-0 last:pb-0">
                <Link
                  to={`/updates/${u.id}`}
                  data-testid={`update-${u.id}`}
                  className="flex items-start gap-3 hover:bg-white/5 -mx-2 px-2 py-2 rounded-sm transition-colors"
                  style={{ minHeight: 56 }}
                >
                  <span
                    data-testid={`update-badge-${u.category}`}
                    className="px-2 py-1 text-[10px] uppercase tracking-widest font-medium shrink-0 mt-0.5"
                    style={{ background: cat.bg, color: cat.color }}
                  >
                    {u.category}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-[16px] text-foreground leading-snug">{headline}</div>
                    <div className="text-[13px] text-foreground/65 mt-0.5 leading-snug">{short}</div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}

export { BADGE as UPDATE_BADGE, pickLang };
